/**
 * /p/:slug — White-label public landing for Premium tenants.
 *
 * Fetches tenant branding from /api/landing/branding/{slug}. If the tenant
 * is not Premium (402) or doesn't exist (404), redirects to the default
 * landing.
 *
 * The landing reuses the same Hero / Pricing / FAQ structure from the main
 * Landing but with overridden colors, logo and headline copy.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Loader2, Sparkles, Zap } from "lucide-react";
import { useABVariant } from "../hooks/useABVariant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_lead-discovery-hub-3/artifacts/wg4n305a_AgenteLead%20Pro%20Logo.png";

const FALLBACK_BRANDING = {
  primary_color: "#0F172A",
  accent_color: "#84CC16",
  logo_url: DEFAULT_LOGO,
  hero_eyebrow: "Local B2B prospecting",
  hero_h1: "Generate qualified leads — fast.",
  hero_sub: "AgenteLead Pro is the sales cockpit your team uses every day. Find. Engage. Close.",
  hero_cta: "Start free trial",
  tagline: "FIND. ENGAGE. GROW.",
};

export default function TenantLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/landing/branding/${encodeURIComponent(slug)}`);
        if (!alive) return;
        setData(data);
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        if (status === 404 || status === 402) {
          // Tenant not found OR not Premium — fall through to the default landing.
          navigate("/", { replace: true });
          return;
        }
        setError(e?.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug, navigate]);

  // Force dark theme while mounted.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "dark");
    return () => { if (prev !== null) root.setAttribute("data-theme", prev); else root.removeAttribute("data-theme"); };
  }, []);

  // Iter57 — inject per-tenant <head> tags: title, description, favicon
  // (uses the tenant logo when set), and OG image (dynamic SVG endpoint).
  useEffect(() => {
    if (!data) return undefined;
    const branding = data.branding || {};
    const apiOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const ogUrl = `${apiOrigin}/api/landing/og/${encodeURIComponent(slug)}`;
    const desc = (branding.hero_sub || `${data.name || slug} — powered by AgenteLead.Pro`).slice(0, 240);
    const prevTitle = document.title;
    document.title = `${data.name || slug} · AgenteLead.Pro`;

    // Helper: ensure a meta tag exists with name|property and a value.
    const setMeta = (key, attr, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"][data-tenant="1"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        el.setAttribute("data-tenant", "1");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };
    const created = [];
    created.push(setMeta("description", "name", desc));
    created.push(setMeta("og:title", "property", data.name || slug));
    created.push(setMeta("og:description", "property", desc));
    created.push(setMeta("og:image", "property", ogUrl));
    created.push(setMeta("og:type", "property", "website"));
    created.push(setMeta("twitter:card", "name", "summary_large_image"));
    created.push(setMeta("twitter:image", "name", ogUrl));
    if (branding.logo_url) {
      created.push(setLink("icon", branding.logo_url));
      created.push(setLink("apple-touch-icon", branding.logo_url));
    }
    return () => {
      document.title = prevTitle;
      // Remove all tenant-injected favicon links; meta tags can stay since
      // they're updated on the next mount anyway.
      document.head.querySelectorAll('link[data-tenant="1"]').forEach((el) => el.remove());
    };
  }, [data, slug]);

  const branding = useMemo(() => ({
    ...FALLBACK_BRANDING, ...((data && data.branding) || {}),
  }), [data]);

  const heroAB = useABVariant("tenant-hero-primary", {
    A: branding.hero_cta || "Start free trial",
    B: "See it find leads in 60s",
  }, { tenantSlug: slug, page: "tenant-landing" });

  if (loading) {
    return (
      <div data-testid="tenant-landing-loading" className="min-h-screen flex items-center justify-center" style={{ background: "#0F172A", color: "#FFFFFF" }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#84CC16" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="tenant-landing-error" className="min-h-screen flex items-center justify-center px-6" style={{ background: "#0F172A", color: "#FFFFFF" }}>
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-black mb-3">Something went wrong</h1>
          <p className="text-[14px]" style={{ color: "#A1A1AA" }}>{error}</p>
          <Link to="/" className="mt-6 inline-block font-bold text-[13px]" style={{ color: "#84CC16" }}>← Back to AgenteLead Pro</Link>
        </div>
      </div>
    );
  }

  const PRIMARY = branding.primary_color || "#0F172A";
  const ACCENT = branding.accent_color || "#84CC16";
  const surface = "#1E293B";

  return (
    <div data-testid="tenant-landing-page"
      className="min-h-screen antialiased font-body"
      style={{ background: PRIMARY, color: "#FFFFFF" }}>
      {/* Sticky nav */}
      <header data-testid="tenant-landing-nav" className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{ backgroundColor: `${PRIMARY}d9`, borderColor: `${ACCENT}33` }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={branding.logo_url || DEFAULT_LOGO} alt={data?.name || "Brand"} className="h-10 w-10 object-contain rounded" />
            <div>
              <div className="font-display text-[18px] font-extrabold tracking-tight leading-none text-white" data-testid="tenant-landing-brand">
                {data?.name || "AgenteLead Pro"}
              </div>
              {branding.tagline && (
                <div className="text-[10px] tracking-[0.22em] font-bold mt-0.5" style={{ color: ACCENT }}>{branding.tagline}</div>
              )}
            </div>
          </div>
          <Link to="/signup"
            data-testid="tenant-landing-cta-header"
            className="inline-flex items-center gap-1.5 text-[13px] font-bold px-4 py-2 rounded-md transition-all hover:-translate-y-0.5"
            style={{ backgroundColor: ACCENT, color: PRIMARY, boxShadow: `0 8px 30px -10px ${ACCENT}88` }}>
            {branding.hero_cta}<ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="tenant-landing-hero" className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full" style={{ background: `radial-gradient(circle, ${ACCENT}22, transparent 60%)` }} />
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            {branding.hero_eyebrow && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ borderColor: `${ACCENT}55`, backgroundColor: `${ACCENT}1a`, color: ACCENT }}>
                <Sparkles className="w-3 h-3" /> {branding.hero_eyebrow}
              </span>
            )}
            <h1 data-testid="tenant-landing-h1" className="font-display mt-6 text-5xl md:text-6xl lg:text-[5rem] font-black tracking-tighter leading-[0.95] text-white">
              {branding.hero_h1}
            </h1>
            {branding.hero_sub && (
              <p className="mt-6 text-[17px] md:text-[19px] leading-relaxed max-w-2xl" style={{ color: "#A1A1AA" }}>
                {branding.hero_sub}
              </p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup"
                data-testid="tenant-landing-cta-primary"
                onClick={heroAB.trackClick}
                data-ab-variant={heroAB.variant}
                className="inline-flex items-center gap-2 font-bold text-[14px] tracking-wide px-7 py-4 rounded-md transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: ACCENT, color: PRIMARY, boxShadow: `0 8px 30px -10px ${ACCENT}88` }}>
                {heroAB.copy}<ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#pricing" className="inline-flex items-center gap-2 border-2 font-bold text-[14px] px-6 py-4 rounded-md transition-all hover:-translate-y-0.5"
                style={{ borderColor: `${ACCENT}66`, color: "#FFFFFF" }}>
                See plans
              </a>
            </div>
            <p className="mt-5 text-[12px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#A1A1AA" }}>
              <Check className="inline w-3.5 h-3.5 mr-1.5" style={{ color: "#10B981" }} />
              No credit card · 14 full days · 2-minute setup
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border p-7 backdrop-blur" style={{ backgroundColor: surface, borderColor: `${ACCENT}33` }}>
              <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] mb-3" style={{ color: ACCENT }}>
                <Zap className="inline w-3 h-3 mr-1" /> Sample lead from your market
              </div>
              <div className="text-[15px] font-bold text-white">Coastal Roofing Co. · Miami, FL</div>
              <div className="text-[12px] mt-1" style={{ color: "#A1A1AA" }}>★ 4.8 · 87 reviews · coastalroofing.com</div>
              <div className="mt-4 h-2 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                <div className="h-full rounded-full" style={{ width: "92%", background: `linear-gradient(to right, #F59E0B, ${ACCENT})` }} />
              </div>
              <div className="mt-2 text-[11px] tabular-nums" style={{ color: ACCENT }}>SCORE 92/100 — HOT</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" data-testid="tenant-landing-pricing" className="py-20 lg:py-28" style={{ backgroundColor: PRIMARY, borderTop: `1px solid ${ACCENT}25` }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl">
            <p className="font-mono text-[10.5px] uppercase tracking-[0.28em]" style={{ color: ACCENT }}>Plans</p>
            <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white">
              Start free. Upgrade when you scale.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { name: "Entry",   price: "$49",  features: ["1k leads/mo", "Google Maps API", "Basic Kanban"] },
              { name: "Pro",     price: "$149", features: ["5k leads/mo", "Unlimited AI audits", "Outreach Sequencer", "5 seats"], popular: true },
              { name: "Premium", price: "$399", features: ["Unlimited leads", "Social Prospecting", "Multi-tenant", "Priority support"] },
            ].map((tier) => (
              <div key={tier.name} data-testid={`tenant-landing-tier-${tier.name.toLowerCase()}`}
                className="relative rounded-2xl border p-7 flex flex-col transition-all hover:-translate-y-1"
                style={{ backgroundColor: surface, borderColor: tier.popular ? ACCENT : `${ACCENT}33`, borderWidth: tier.popular ? 2 : 1 }}>
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.18em]"
                    style={{ backgroundColor: ACCENT, color: PRIMARY }}>★ Most Popular</div>
                )}
                <h3 className="font-display text-2xl font-black text-white">{tier.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-black" style={{ color: tier.popular ? ACCENT : "#FFFFFF" }}>{tier.price}</span>
                  <span className="text-[13px]" style={{ color: "#A1A1AA" }}>/mo</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13.5px] text-white">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: tier.popular ? ACCENT : "#10B981" }} />{f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" data-testid={`tenant-landing-cta-${tier.name.toLowerCase()}`}
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 font-bold text-[13px] py-3 rounded-md"
                  style={{ backgroundColor: tier.popular ? ACCENT : "transparent", color: tier.popular ? PRIMARY : ACCENT, border: tier.popular ? "none" : `1.5px solid ${ACCENT}` }}>
                  Start trial<ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 text-center text-[12px]" style={{ borderTop: `1px solid ${ACCENT}25`, color: "#71717A" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          {data?.name} · powered by <Link to="/" className="hover:text-white" style={{ color: ACCENT }}>AgenteLead Pro</Link>
          <div className="mt-2">© 2026 — all rights reserved</div>
        </div>
      </footer>
    </div>
  );
}
